import React, { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X, Loader2, Pencil, Droplets, PawPrint, Upload, AlertTriangle, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const RATINGS = [
  { value: "accessible", label: "Dog-friendly route" },
  { value: "difficult", label: "Stiles on route" },
  { value: "restricted", label: "Dogs not permitted" },
  { value: "unknown", label: "Unknown" },
];

/**
 * Admin-only modal for editing a trail's curatable fields.
 * Shown from LocationDetail when the signed-in user is an admin.
 */
export default function EditTrailModal({ trail, onClose, onSaved }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: trail.name || "",
    region: trail.region || "",
    short_desc: trail.short_desc || "",
    water_source_available: !!trail.water_source_available,
    dog_friendly_rating: trail.dog_friendly_rating || "unknown",
    editor_pick: !!trail.editor_pick,
    ozzy_verified: !!trail.ozzy_verified,
  });
  // Pending GPX file → shown as a summary chip before the admin
  // confirms the destructive replace. Kept in local state so the admin
  // can back out without any side effects.
  const gpxInputRef = useRef(null);
  const [pendingGpx, setPendingGpx] = useState(null); // { file, name, pointCount }
  const [confirmReplace, setConfirmReplace] = useState(false);
  const [confirmUndo, setConfirmUndo] = useState(false);

  // Route history — surfaces the most recent snapshot so the admin can
  // one-click roll back a bad GPX replace. Refetches after any replace
  // or undo so the button state stays in sync with the server.
  const historyQ = useQuery({
    queryKey: ["trail-route-history", trail.location_id],
    queryFn: async () =>
      (await api.get(`/admin/trails/${trail.location_id}/route-history`)).data,
    staleTime: 30_000,
  });
  const latestSnapshot = historyQ.data?.items?.[0] || null;
  const currentRoute = historyQ.data?.current || null;
  // Undo-diff — compare the live route against what the newest snapshot
  // would restore. Only compute when both halves are present so we don't
  // render a half-filled line.
  const undoDiff = React.useMemo(() => {
    if (!latestSnapshot || !currentRoute) return null;
    const nowPts = currentRoute.point_count ?? 0;
    const nowKm = Number(currentRoute.distance_km ?? 0);
    const afterPts = latestSnapshot.point_count ?? 0;
    const afterKm = Number(latestSnapshot.distance_km ?? 0);
    // "Big rollback" = distance change > 50% relative to the larger side
    // (symmetric so a 2 km → 4 km restore flags too, not just 4 → 2).
    const larger = Math.max(nowKm, afterKm);
    const bigChange = larger > 0 && Math.abs(nowKm - afterKm) / larger > 0.5;
    return { nowPts, nowKm, afterPts, afterKm, bigChange };
  }, [latestSnapshot, currentRoute]);

  const mut = useMutation({
    mutationFn: async () => (await api.patch(`/admin/trails/${trail.location_id}`, form)).data,
    onSuccess: (data) => {
      toast.success("Trail updated");
      qc.invalidateQueries({ queryKey: ["loc", trail.location_id] });
      qc.invalidateQueries({ queryKey: ["locations"] });
      onSaved?.(data.trail);
      onClose?.();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Couldn't update trail"),
  });

  // GPX replace — separate mutation so the primary Save Changes button
  // and the Replace Route flow don't share pending / disabled state.
  const gpxMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post(
        `/admin/trails/${trail.location_id}/replace-gpx`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return res.data;
    },
    onSuccess: (data) => {
      toast.success(
        `Route replaced — ${data.point_count} points · ${data.distance_km.toFixed(2)} km`,
      );
      setPendingGpx(null);
      setConfirmReplace(false);
      // Force everywhere that renders this trail's polyline to refetch.
      qc.invalidateQueries({ queryKey: ["loc", trail.location_id] });
      qc.invalidateQueries({ queryKey: ["locations"] });
      qc.invalidateQueries({ queryKey: ["trail-route-history", trail.location_id] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Couldn't replace GPX");
      setConfirmReplace(false);
    },
  });

  // Undo — restore the newest snapshot from trail_route_history. The
  // backend consumes the snapshot on success, so repeat clicks walk
  // further back through the stack.
  const undoMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/admin/trails/${trail.location_id}/undo-route`)).data,
    onSuccess: (data) => {
      const km = data.distance_km != null ? ` · ${data.distance_km.toFixed(2)} km` : "";
      toast.success(`Previous route restored — ${data.point_count} points${km}`);
      setConfirmUndo(false);
      qc.invalidateQueries({ queryKey: ["loc", trail.location_id] });
      qc.invalidateQueries({ queryKey: ["locations"] });
      qc.invalidateQueries({ queryKey: ["trail-route-history", trail.location_id] });
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Couldn't undo the last route change");
      setConfirmUndo(false);
    },
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onGpxPicked = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".gpx")) {
      toast.error("Please pick a .gpx file.");
      ev.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("GPX file is too large (max 5 MB).");
      ev.target.value = "";
      return;
    }
    // Client-side peek so the admin sees the point count BEFORE they
    // commit — matches the pattern already used by /admin/trails/new.
    let pointCount = 0;
    try {
      const text = await file.text();
      const doc = new DOMParser().parseFromString(text, "application/xml");
      pointCount =
        doc.getElementsByTagName("trkpt").length +
        doc.getElementsByTagName("rtept").length;
      if (pointCount === 0) pointCount = doc.getElementsByTagName("wpt").length;
    } catch {
      // Parse errors surface as a backend 400 on submit — no need to
      // block the admin from previewing an oddball file locally.
    }
    setPendingGpx({ file, name: file.name, pointCount });
    ev.target.value = "";
  };

  return (
    <>
      {/* Backdrop — separate fixed layer (same pattern as ScoutRatingModal)
          so the modal owns its viewport anchoring on mobile. */}
      <div
        className="fixed inset-0 overflow-hidden z-[1100] bg-brand-text/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="edit-trail-backdrop"
      />
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-trail-modal"
        // Mobile: pinned into safe viewport space; desktop (md+): centred card.
        className="fixed inset-x-4 top-2 bottom-12 z-[1101] flex flex-col overflow-hidden md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-md md:h-auto md:max-h-[85vh] bg-brand-surface rounded-3xl shadow-2xl border border-brand-border/60 animate-fade-up"
      >
        {/* Header — fixed at top */}
        <div className="p-6 pb-3 border-b border-brand-border/50 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-heading text-xl text-brand-text flex items-center gap-2">
              <Pencil size={18} className="text-brand-primary" />
              Edit trail
            </h3>
            <button onClick={onClose} className="text-brand-subtle" aria-label="Close" data-testid="edit-trail-close">
              <X size={20} />
            </button>
          </div>
          <p className="text-xs text-brand-subtle truncate">{trail.location_id}</p>
        </div>

        {/* Scrolling middle — min-h-0 so the flex column can actually shrink */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 pt-4" data-testid="edit-trail-form">

        <label className="block text-sm font-medium text-brand-text">Name</label>
        <input
          data-testid="edit-trail-name"
          value={form.name}
          onChange={(e) => set("name", e.target.value)}
          className="td-input mt-1"
        />

        <label className="block text-sm font-medium text-brand-text mt-4">Region</label>
        <input
          data-testid="edit-trail-region"
          value={form.region}
          onChange={(e) => set("region", e.target.value)}
          className="td-input mt-1"
        />

        <label className="block text-sm font-medium text-brand-text mt-4">Short description</label>
        <textarea
          data-testid="edit-trail-desc"
          value={form.short_desc}
          onChange={(e) => set("short_desc", e.target.value)}
          rows={3}
          maxLength={500}
          className="td-input mt-1 resize-none"
        />

        <label className="block text-sm font-medium text-brand-text mt-4">Dog-friendliness</label>
        <select
          data-testid="edit-trail-rating"
          value={form.dog_friendly_rating}
          onChange={(e) => set("dog_friendly_rating", e.target.value)}
          className="td-input mt-1"
        >
          {RATINGS.map((r) => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>

        {/* Hydration Helper */}
        <label
          className="mt-4 flex items-start gap-3 p-3 rounded-2xl border border-brand-border cursor-pointer hover:border-brand-primary/40 transition-colors"
          data-testid="edit-trail-water-label"
        >
          <input
            data-testid="edit-trail-water"
            type="checkbox"
            checked={form.water_source_available}
            onChange={(e) => set("water_source_available", e.target.checked)}
            className="mt-1 w-4 h-4 accent-brand-primary"
          />
          <span className="flex-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-brand-text">
              <Droplets size={14} className="text-[#2E7AB8]" /> Water source available
            </span>
            <span className="block text-xs text-brand-subtle mt-0.5">
              Tick if there&apos;s a stream, tap, or natural water Ozzy can drink from along this trail.
            </span>
          </span>
        </label>

        {/* Ozzy Verified */}
        <label
          className="mt-3 flex items-start gap-3 p-3 rounded-2xl border border-brand-border cursor-pointer hover:border-brand-accent/40 transition-colors"
          data-testid="edit-trail-ozzy-label"
        >
          <input
            data-testid="edit-trail-ozzy"
            type="checkbox"
            checked={form.ozzy_verified}
            onChange={(e) => set("ozzy_verified", e.target.checked)}
            className="mt-1 w-4 h-4 accent-brand-accent"
          />
          <span className="flex-1">
            <span className="flex items-center gap-1.5 text-sm font-medium text-brand-text">
              <PawPrint size={14} className="text-brand-accent" /> Ozzy Verified
            </span>
            <span className="block text-xs text-brand-subtle mt-0.5">
              Mark this trail as personally walked &amp; approved — a special badge will appear everywhere it&apos;s listed.
            </span>
          </span>
        </label>

        <label className="mt-3 flex items-center gap-3 px-3 py-2 cursor-pointer">
          <input
            data-testid="edit-trail-editor"
            type="checkbox"
            checked={form.editor_pick}
            onChange={(e) => set("editor_pick", e.target.checked)}
            className="w-4 h-4 accent-brand-accent"
          />
          <span className="text-sm text-brand-text">Editor&apos;s pick</span>
        </label>

        {/* GPX replacement — destructive, so it lives below the editable
            metadata and requires a confirmation modal before firing. All
            other trail data (reviews, hazards, photos, achievements,
            ratings) is preserved server-side by design. */}
        <div
          className="mt-6 pt-4 border-t border-brand-border/60"
          data-testid="edit-trail-gpx-section"
        >
          <div className="flex items-center gap-2">
            <Upload size={14} className="text-brand-primary" />
            <span className="text-sm font-medium text-brand-text">Trail route (GPX)</span>
          </div>
          <p className="text-xs text-brand-subtle mt-1 leading-relaxed">
            Upload a new .gpx file to overwrite this trail&apos;s path. Distance and
            start/finish pins are recomputed. Name, description, reviews, photos,
            hazards, and scout data are kept exactly as-is.
          </p>
          <input
            ref={gpxInputRef}
            type="file"
            accept=".gpx,application/gpx+xml,application/xml,text/xml"
            onChange={onGpxPicked}
            className="hidden"
            data-testid="edit-trail-gpx-input"
          />
          {pendingGpx ? (
            <div
              className="mt-3 rounded-2xl border border-brand-primary/30 bg-brand-primary/5 p-3"
              data-testid="edit-trail-gpx-pending"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-brand-text truncate" data-testid="edit-trail-gpx-name">
                    {pendingGpx.name}
                  </div>
                  <div className="text-xs text-brand-subtle mt-0.5">
                    {pendingGpx.pointCount > 0
                      ? `${pendingGpx.pointCount.toLocaleString("en-GB")} points detected`
                      : "Point count unknown — the backend parser will validate."}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setPendingGpx(null)}
                  className="text-brand-subtle hover:text-brand-error shrink-0"
                  aria-label="Cancel GPX upload"
                  data-testid="edit-trail-gpx-clear"
                >
                  <X size={16} />
                </button>
              </div>
              <button
                type="button"
                onClick={() => setConfirmReplace(true)}
                disabled={gpxMut.isPending}
                className="td-btn-primary mt-3 w-full !py-2 !px-4 !text-sm !bg-brand-error whitespace-nowrap"
                data-testid="edit-trail-gpx-replace"
              >
                {gpxMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <AlertTriangle size={14} />}
                Replace route with this file
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => gpxInputRef.current?.click()}
              disabled={gpxMut.isPending}
              className="td-btn-secondary mt-3 w-full !py-2 !px-4 !text-sm whitespace-nowrap"
              data-testid="edit-trail-gpx-pick"
            >
              <Upload size={14} /> Upload new GPX
            </button>
          )}

          {/* Undo — surfaces only when we actually have a snapshot to
              restore. Kept below the upload flow so the primary "make a
              change" affordance stays top. */}
          {latestSnapshot && (
            <div
              className="mt-3 rounded-2xl border border-brand-border bg-brand-sand/50 p-3"
              data-testid="edit-trail-undo-section"
            >
              <div className="flex items-start gap-2">
                <Undo2 size={14} className="text-brand-primary mt-0.5 shrink-0" />
                <div className="text-xs text-brand-subtle leading-relaxed flex-1 min-w-0">
                  <span className="text-brand-text font-medium">Previous route available</span>
                  {" — "}
                  <span data-testid="edit-trail-undo-meta">
                    {(latestSnapshot.point_count ?? 0).toLocaleString("en-GB")} pts saved{" "}
                    {latestSnapshot.created_at
                      ? new Date(latestSnapshot.created_at).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                          year: "numeric",
                        })
                      : ""}
                  </span>
                </div>
              </div>
              {undoDiff && (
                <div
                  data-testid="edit-trail-undo-diff"
                  data-big-change={undoDiff.bigChange ? "true" : "false"}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-brand-border bg-brand-surface px-3 py-1 text-[11px] text-brand-subtle"
                >
                  {undoDiff.bigChange && (
                    <AlertTriangle
                      size={11}
                      className="text-brand-error shrink-0"
                      data-testid="edit-trail-undo-diff-warn"
                    />
                  )}
                  <span className="truncate">
                    <span className="text-brand-text">
                      Now {undoDiff.nowPts.toLocaleString("en-GB")} pts,{" "}
                      {undoDiff.nowKm.toFixed(2)} km
                    </span>
                    <span className="opacity-60"> → </span>
                    <span>
                      After undo {undoDiff.afterPts.toLocaleString("en-GB")} pts,{" "}
                      {undoDiff.afterKm.toFixed(2)} km
                    </span>
                  </span>
                </div>
              )}
              <button
                type="button"
                onClick={() => setConfirmUndo(true)}
                disabled={undoMut.isPending || gpxMut.isPending}
                className="td-btn-secondary mt-2 w-full !py-2 !px-4 !text-sm whitespace-nowrap !text-brand-primary !border-brand-primary/40"
                data-testid="edit-trail-undo-btn"
              >
                {undoMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
                Undo last route change
              </button>
            </div>
          )}
        </div>
        </div>

        {/* Footer — shrink-0 so Save/Cancel stay permanently docked and
            visible above the mobile bottom-nav / safari toolbar. */}
        <div
          className="shrink-0 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] bg-brand-surface border-t border-brand-border/50"
          data-testid="edit-trail-footer"
        >
          <div className="flex items-center gap-2">
            <button
              data-testid="edit-trail-cancel"
              type="button"
              onClick={onClose}
              className="td-btn-secondary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              data-testid="edit-trail-save"
              type="button"
              onClick={() => mut.mutate()}
              disabled={mut.isPending}
              className="td-btn-primary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
            >
              {mut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Pencil size={14} />}
              Save changes
            </button>
          </div>
        </div>
      </div>

      {/* GPX replace confirmation — destructive so we always double-check.
          Only reviews / hazards / photos are safe; the route itself will be
          overwritten and cannot be recovered without the previous GPX. */}
      {confirmReplace && pendingGpx && (
        <div
          className="fixed inset-0 z-[1200] bg-brand-text/70 flex items-center justify-center p-4"
          onClick={() => !gpxMut.isPending && setConfirmReplace(false)}
          data-testid="edit-trail-gpx-confirm-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-brand-surface rounded-3xl w-full max-w-sm p-6 animate-fade-up"
            data-testid="edit-trail-gpx-confirm-modal"
          >
            <h3 className="font-heading text-xl text-brand-text flex items-center gap-2">
              <AlertTriangle size={18} className="text-brand-error" />
              Replace this trail&apos;s route?
            </h3>
            <p className="text-sm text-brand-subtle mt-2">
              The existing path for <span className="text-brand-text font-medium break-words">{trail.name || trail.location_id}</span> will
              be overwritten by <span className="text-brand-text font-medium break-words">{pendingGpx.name}</span>. Reviews, hazards,
              photos, and every other trail detail stay untouched — but the previous route
              cannot be recovered.
            </p>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmReplace(false)}
                disabled={gpxMut.isPending}
                data-testid="edit-trail-gpx-confirm-cancel"
                className="td-btn-secondary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => gpxMut.mutate(pendingGpx.file)}
                disabled={gpxMut.isPending}
                data-testid="edit-trail-gpx-confirm-submit"
                className="td-btn-primary flex-1 !py-2.5 !px-4 !text-sm !bg-brand-error whitespace-nowrap"
              >
                {gpxMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                Yes, replace route
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo confirmation — non-destructive but still a route-level
          change, so we double-check. Names the trail and the snapshot
          date so the admin knows exactly what they're rolling back to. */}
      {confirmUndo && latestSnapshot && (
        <div
          className="fixed inset-0 z-[1200] bg-brand-text/70 flex items-center justify-center p-4"
          onClick={() => !undoMut.isPending && setConfirmUndo(false)}
          data-testid="edit-trail-undo-confirm-backdrop"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-brand-surface rounded-3xl w-full max-w-sm p-6 animate-fade-up"
            data-testid="edit-trail-undo-confirm-modal"
          >
            <h3 className="font-heading text-xl text-brand-text flex items-center gap-2">
              <Undo2 size={18} className="text-brand-primary" />
              Undo last route change?
            </h3>
            <p className="text-sm text-brand-subtle mt-2">
              The current path for{" "}
              <span className="text-brand-text font-medium break-words">{trail.name || trail.location_id}</span>{" "}
              will be replaced with the previously-saved route
              {" ("}
              {(latestSnapshot.point_count ?? 0).toLocaleString("en-GB")} pts
              {latestSnapshot.created_at ? `, saved ${new Date(latestSnapshot.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}
              {"). "}
              Reviews, hazards, photos, and every other trail detail stay untouched.
            </p>
            <div className="flex gap-2 mt-5">
              <button
                type="button"
                onClick={() => setConfirmUndo(false)}
                disabled={undoMut.isPending}
                data-testid="edit-trail-undo-confirm-cancel"
                className="td-btn-secondary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => undoMut.mutate()}
                disabled={undoMut.isPending}
                data-testid="edit-trail-undo-confirm-submit"
                className="td-btn-primary flex-1 !py-2.5 !px-4 !text-sm whitespace-nowrap"
              >
                {undoMut.isPending ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />}
                Yes, undo
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
